import { getDefaultViewData } from '@realestate.com.au/iglu-data';
import { get } from 'lodash';
import {
  PAGE_TYPE_SEARCH_RESULT,
  PAGE_TYPE_HYBRID_SEARCH_RESULT,
  TEALIUM_SCHEMA_FIRST_SEARCH_RESULT,
  TEALIUM_SCHEMA_LISTING_SEARCH_PARAMETERS,
  TEALIUM_SCHEMA_LISTING_SEARCH_RESULTS,
  TEALIUM_SCHEMA_NEW_NEARBY_PROPERTY_IMPRESSION,
  TEALIUM_SITE,
  TEALIUM_SCHEMA_GET_ALERTS,
  TEALIUM_SCHEMA_MODAL_OPEN,
  TEALIUM_SCHEMA_TRACK_IMAGE_CAROUSEL,
  TEALIUM_SCHEMA_TRACK_SET_MY_YIELD,
  USER_ACTION_SETVALUE,
  TEALIUM_SCHEMA_EXPERIMENTS,
  PAGE_TYPE_SEARCH_RESULT_MAP,
  CAMPAIGN_SOURCE_RCA,
  CAMPAIGN_NAME_EXTRA_LOCATION,
  TEALIUM_SCHEMA_TOOLTIP_DISPLAYED,
  TEALIUM_SCHEMA_FLOATING_PILL_INTERACTION,
} from '../../../shared/tracking/tealium/constants';
import {
  trackEmailLeadToAgentEvent,
  trackAppPromotionLinkClick,
  trackEmailOpenClick,
  trackSavePropertyEvent as trackSaveListing,
} from '../../../shared/tracking/tealium/tracking';
import {
  getTrackingSource,
  setTrackingSource,
} from '../../../client/tracking/tealium/sourceTracking';
import {
  extractKeywordsData,
  extractLocalities,
  extractLocationsRequested,
  extractSearchResultsListingProps,
  extractSecondaryValues,
  extractSortBy,
} from '../../../shared/tracking/propExtractor';
import { MAP_HYBRID, MAP_FULL } from '../model/query/mapViewOptions';
import tealiumWrapper from '../../../shared/tracking/tealium/tealiumWrapper';
import {
  constructUserData,
  constructCampaignTrackingCode,
} from '../../../shared/tracking/tealium/trackingUtils';
import { allPropertyTypes } from '../../../shared/model/constants/propertyTypes';
import { generateSearchId } from '../../../client/cookie/generateSearchId';

export const buildPageSource = (channel, isHybrid) =>
  `${TEALIUM_SITE}:${channel.tealium}:${
    isHybrid ? PAGE_TYPE_HYBRID_SEARCH_RESULT : PAGE_TYPE_SEARCH_RESULT
  }`;

const buildPageSubSection = (channel, isMapViewPage = false) =>
  `${TEALIUM_SITE}:${channel.tealium}:${
    isMapViewPage ? PAGE_TYPE_SEARCH_RESULT_MAP : PAGE_TYPE_SEARCH_RESULT
  }`;

const buildPageType = (refinements) =>
  (refinements.mapView ? PAGE_TYPE_SEARCH_RESULT_MAP : PAGE_TYPE_SEARCH_RESULT);

const getHybridMapView = (refinements) => {
  switch (refinements.mapView) {
    case MAP_HYBRID:
      return true;
    case MAP_FULL:
      return false;
    default:
      return undefined;
  }
};

export const buildExtraLocationCampaignTrackingUrl = ({ pdpUrl, listingId, campaignPlacement }) => {
  const campaignTrackingCode = constructCampaignTrackingCode(
    CAMPAIGN_SOURCE_RCA,
    CAMPAIGN_NAME_EXTRA_LOCATION,
    campaignPlacement,
    `listingid_${listingId}`,
  );

  return `${pdpUrl}${campaignTrackingCode}`;
};

const trackSRPPageEvent = ({
  refinements,
  exactMatchingListings,
  surroundingSuburbListings,
  availableResults,
  resolvedLocations,
  advertising,
  experimentsManager,
  autoSearch,
  marketInsightsShown,
}) => {
  const siteSection = refinements.channelObject.tealium;
  const pageNumber = refinements.page;
  const pageType = buildPageType(refinements);
  const searchResultsListings = [...exactMatchingListings, ...surroundingSuburbListings];
  const { element, page } = getTrackingSource();
  const maybeExperiments = experimentsManager?.getAllExperimentsForTracking();
  const isHybridMapView = getHybridMapView(refinements);
  const isMapView = isHybridMapView !== undefined;

  const defaultViewData = getDefaultViewData({
    page: {
      pageNameOverride: `${TEALIUM_SITE}:${siteSection}:${pageType}`,
      site: TEALIUM_SITE,
      pageType,
      siteSection,
      siteSubSection: buildPageSubSection(refinements.channelObject, isMapView),
      clickThroughSourceElement: element,
      clickThroughSourcePage: page,
    },
  });

  const buildSearchParameters = () => ({
    sort_by: extractSortBy(refinements.activeSort),
    price_min: refinements.minPrice && +refinements.minPrice,
    price_max: refinements.maxPrice && +refinements.maxPrice,
    land_min: refinements.minSiteArea && +refinements.minSiteArea,
    land_max: refinements.maxSiteArea && +refinements.maxSiteArea,
    floor_area_min: refinements.minFloorArea && +refinements.minFloorArea,
    floor_area_max: refinements.maxFloorArea && +refinements.maxFloorArea,
    tenure_type: refinements.tenureTypeObject.omniture,
    refinements_and_keywords: extractKeywordsData(refinements.keywords),
    localities: extractLocalities(resolvedLocations),
    locations_requested: extractLocationsRequested(refinements.localities, refinements.boundingBox),
    exclude_under_contract: false,
    car_spaces_min: refinements.numParkingSpaces,
    nabers_energy_rating_min: refinements.energyEfficiency,
    property_type: refinements.propertyTypes?.map((item) => item.id),
    extended_search: refinements.includePropertiesWithin?.replace('surrounding', ' surrounding'),
    hybrid_map_view: isHybridMapView,
    auto_search: isMapView ? autoSearch : undefined,
  });

  const listingSearchParameters = () => ({
    listing_search_parameters: {
      schema: TEALIUM_SCHEMA_LISTING_SEARCH_PARAMETERS,
      data: buildSearchParameters(),
    },
  });

  const listingSearchParametersWithChannel = () => ({
    ...buildSearchParameters(),
    channel: refinements.channelObject.id,
  });

  const listingSearchResultsData = () => ({
    listing_search_results: {
      schema: TEALIUM_SCHEMA_LISTING_SEARCH_RESULTS,
      data: {
        listings_total: availableResults,
        page_number: pageNumber,
        listings: extractSearchResultsListingProps(searchResultsListings),
        search_id: generateSearchId(JSON.stringify(listingSearchParametersWithChannel())),
        ...(marketInsightsShown === true && { market_insights_shown: true }),
      },
    },
  });

  const firstListing = [...searchResultsListings, undefined][0];
  const firstSearchResult = () => ({
    first_search_result: {
      schema: TEALIUM_SCHEMA_FIRST_SEARCH_RESULT,
      data: {
        agency_id: firstListing && get(firstListing.agencies, '[0].id'),
        secondary_agency_ids: firstListing && extractSecondaryValues(firstListing.agencies, 'id'),
        car_spaces: firstListing?.attributes?.carSpaces,
        product_depth: firstListing?.product,
        property_type: allPropertyTypes.find(
          (propertyType) =>
            propertyType.displayText === get(firstListing, 'attributes.propertyTypes[0]'),
        )?.id,
        secondary_property_types:
          firstListing && extractSecondaryValues(firstListing.attributes?.propertyTypes || []),
        price: advertising?.price,
        marketing_region: advertising?.region,
        state: advertising?.state,
        suburb: firstListing?.address?.suburb,
        postcode: advertising?.postcode,
        floor_area: advertising?.floorArea,
        land_area: advertising?.landArea,
        marketing_price_range: advertising?.advertisingRange,
      },
    },
  });

  const experimentsData = () => ({
    experiments: {
      schema: TEALIUM_SCHEMA_EXPERIMENTS,
      data: {
        experiments: maybeExperiments,
      },
    },
  });

  tealiumWrapper.trackView([
    ...defaultViewData,
    constructUserData,
    listingSearchResultsData,
    listingSearchParameters,
    firstSearchResult,
    ...(maybeExperiments ? [experimentsData] : []),
  ]);
};

const trackEmailSubmissionSuccess = ({
  agencies,
  agency,
  listingId,
  product,
  tenureTypeObject,
}) => {
  const { responses, selectedContact } = agency;
  trackEmailLeadToAgentEvent({
    agencies,
    listingId,
    responses,
    selectedContact,
    product,
    tenureTypeObject,
  });
};

const trackNearbyPropertyImpressionEvent = (nearbyListings, listingTotal) => {
  const impressionListings = nearbyListings.map((listing) => ({
    listing_id: listing.id,
    agency_id: listing.agencyId,
    secondary_agency_ids: listing.secondaryAgencyIds,
    position: listing.position,
  }));
  tealiumWrapper.trackEvent(() => ({
    schema: TEALIUM_SCHEMA_NEW_NEARBY_PROPERTY_IMPRESSION,
    data: {
      event_name: 'nearby_property_impression',
      listings_total: listingTotal,
      listings: impressionListings,
    },
  }));
};

const trackSetAlert = ({ userAction, frequency }) => {
  tealiumWrapper.trackEvent(() => ({
    schema: TEALIUM_SCHEMA_GET_ALERTS,
    data: {
      event_name: 'get_alert',
      user_action: userAction,
      frequency,
    },
  }));
};

const trackModalClick = (intent) => {
  tealiumWrapper.trackEvent(() => ({
    schema: TEALIUM_SCHEMA_MODAL_OPEN,
    data: {
      event_name: 'my_rea_modal_open',
      intent,
    },
  }));
};

const trackSignInModalClick = () => {
  trackModalClick('sign_in');
};

const trackCreateAccountModalClick = () => {
  trackModalClick('join');
};

const trackMarketInsightsSignInOrJoinModalClick = () => {
  trackModalClick('market_insights');
};

const trackEnquiryFormSignedInModalClick = () => {
  trackModalClick('post_enquiry_registration_sign_in');
};

const trackEnquiryFormJoinModalClick = () => {
  trackModalClick('post_enquiry_registration_join');
};

const trackSRPCarouselClickEvent = ({ listingId, agencies, product, tenureTypeObject }) => {
  tealiumWrapper.trackEvent(() => ({
    schema: TEALIUM_SCHEMA_TRACK_IMAGE_CAROUSEL,
    data: {
      event_name: 'search_result_carousel_view',
      listing_id: listingId,
      agency_id: agencies[0].id,
      secondary_agency_ids: agencies.slice(1).map((agency) => agency.id),
      slide: {
        type: 'photo',
      },
      product_depth: product,
      tenure_type: tenureTypeObject?.omniture,
    },
  }));
};

const trackSetMyYield = ({ yieldOnlyForUnpricedListings, yieldValue, pageSource }) => {
  tealiumWrapper.trackEvent(() => ({
    schema: TEALIUM_SCHEMA_TRACK_SET_MY_YIELD,
    data: {
      event_name: 'set_yield_value',
      user_action: USER_ACTION_SETVALUE,
      my_yield_percentage: yieldValue,
      apply_properties_without_price: yieldOnlyForUnpricedListings,
      in_page_source: pageSource,
    },
  }));
};

const trackNearbyPropertyListingClickThroughSource = ({
  channelObject,
  position,
  listingsTotal,
}) => {
  setTrackingSource({
    page: buildPageSubSection(channelObject),
    element: `nearby-properties:${position}-${listingsTotal}`,
  });
};

const trackSRPClickThroughSource = ({ channelObject, element, isHybrid }) => {
  setTrackingSource({
    page: buildPageSource(channelObject, isHybrid),
    element,
  });
};

const trackMapViewClickThroughSource = ({ channelObject, name, isMapViewPage }) => {
  setTrackingSource({
    page: buildPageSubSection(channelObject, isMapViewPage),
    element: name,
  });
};

const trackFindAgencyClickThroughSource = ({ channelObject, clickThroughSource }) => {
  setTrackingSource({
    page: buildPageSource(channelObject, false),
    element: clickThroughSource,
  });
};

const trackTooltipOpen = (descriptor, product) => {
  tealiumWrapper.trackEvent(() => ({
    schema: TEALIUM_SCHEMA_TOOLTIP_DISPLAYED,
    data: {
      event_name: 'tooltip_displayed',
      descriptor,
      product,
    },
  }));
};

const trackFloatingPillClick = (interaction) => {
  tealiumWrapper.trackEvent(() => ({
    schema: TEALIUM_SCHEMA_FLOATING_PILL_INTERACTION,
    data: {
      event_name: 'floating_pill_interaction',
      user_action: 'click',
      cta: interaction,
    },
  }));
};

export default {
  trackSRPPageEvent,
  trackEmailSubmissionSuccess,
  trackNearbyPropertyImpressionEvent,
  trackAppPromotionLinkClick,
  trackSetAlert,
  trackSRPEmailOpenClick: trackEmailOpenClick,
  trackSaveListing,
  trackSRPCarouselClickEvent,
  trackSetMyYield,
  trackNearbyPropertyListingClickThroughSource,
  trackSRPClickThroughSource,
  trackSignInModalClick,
  trackCreateAccountModalClick,
  trackMarketInsightsSignInOrJoinModalClick,
  trackEnquiryFormSignedInModalClick,
  trackEnquiryFormJoinModalClick,
  trackMapViewClickThroughSource,
  trackFindAgencyClickThroughSource,
  trackTooltipOpen,
  trackFloatingPillClick,
};
